import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "./AdminEmails.css";

/* ---------------------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------------------- */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function emailDocId(email) {
  return normalizeEmail(email).replace(/\//g, "_");
}

const SYNTHETIC_STUDENT_DOMAIN = "@sbl.school";

function isStudentRole(role) {
  return String(role || "").toLowerCase().includes("student");
}

/** Student/child accounts never enter the directory. */
function isExcludedFromDirectory(user) {
  const email = normalizeEmail(user.email);
  if (!email || !EMAIL_RE.test(email)) return true;
  if (email.endsWith(SYNTHETIC_STUDENT_DOMAIN)) return true;
  if (isStudentRole(user.role)) return true;
  if (String(user.view || "").toLowerCase() === "student") return true;
  return false;
}

function fmtDate(value) {
  if (!value) return "—";
  try {
    if (value?.toDate) return value.toDate().toLocaleDateString();
    return new Date(value).toLocaleDateString();
  } catch {
    return "—";
  }
}

function csvEscape(value) {
  const s = String(value ?? "");
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function downloadCsv(filename, rows, headers) {
  const body = rows.map((r) => headers.map((h) => csvEscape(r[h])).join(","));
  const blob = new Blob([[headers.join(","), ...body].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Accepts either a pasted list of emails (one per line) or CSV with a header
 * row. Recognised CSV columns: email, firstName, lastName, schoolName,
 * schoolDistrict, state, notes, source.
 */
function parseContactBlob(text) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  if (lines.length === 0) return [];

  const first = lines[0].toLowerCase();
  const looksLikeHeader = first.includes("email") && first.includes(",");

  if (!looksLikeHeader) {
    return lines
      .map((line) => {
        // tolerate "Name <email@x.com>" and bare "email@x.com"
        const match = line.match(/<([^>]+)>/);
        const email = normalizeEmail(match ? match[1] : line.split(",")[0]);
        return EMAIL_RE.test(email) ? { email } : null;
      })
      .filter(Boolean);
  }

  const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
  return lines
    .slice(1)
    .map((line) => {
      const cells = line.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
      const row = {};
      headers.forEach((h, i) => {
        row[h] = cells[i] || "";
      });
      const email = normalizeEmail(row.email || row.Email || "");
      if (!EMAIL_RE.test(email)) return null;
      return {
        email,
        firstName: row.firstName || row.first || "",
        lastName: row.lastName || row.last || "",
        schoolName: row.schoolName || row.school || "",
        schoolDistrict: row.schoolDistrict || row.district || "",
        state: row.state || "",
        notes: row.notes || "",
        source: row.source || "",
      };
    })
    .filter(Boolean);
}

const BLANK_CONTACT = {
  email: "",
  firstName: "",
  lastName: "",
  schoolName: "",
  schoolDistrict: "",
  state: "",
  source: "outreach",
  notes: "",
};

/* ---------------------------------------------------------------------------
 * Component
 * ------------------------------------------------------------------------- */

const AdminEmails = () => {
  const db = getFirestore();
  const navigate = useNavigate();

  const [entries, setEntries] = useState([]);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [autoSyncDone, setAutoSyncDone] = useState(false);
  const [autoSyncNote, setAutoSyncNote] = useState("");
  const [tab, setTab] = useState("users"); // users | nonUsers | backups
  const [search, setSearch] = useState("");
  const [showDeleted, setShowDeleted] = useState(true);

  const [showAdd, setShowAdd] = useState(false);
  const [newContact, setNewContact] = useState(BLANK_CONTACT);

  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState("");
  const [importPreview, setImportPreview] = useState([]);

  const [editing, setEditing] = useState(null);

  /* ----- load ------------------------------------------------------------ */

  const loadDirectory = useCallback(async () => {
    setLoading(true);
    try {
      const [dirSnap, backupSnap] = await Promise.all([
        getDocs(collection(db, "emailDirectory")),
        getDocs(collection(db, "educatorBackups")),
      ]);
      setEntries(dirSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setBackups(backupSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Failed to load email directory:", err);
      alert(
        "Could not load the email directory. Make sure the Firestore rules for " +
          "emailDirectory and educatorBackups are deployed."
      );
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    loadDirectory();
  }, [loadDirectory]);

  /* ----- derived --------------------------------------------------------- */

  const matchesSearch = useCallback(
    (row) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return [
        row.email,
        row.firstName,
        row.lastName,
        row.schoolName,
        row.schoolDistrict,
        row.state,
        row.role,
        row.educatorCode,
        row.notes,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    },
    [search]
  );

  const userEntries = useMemo(
    () =>
      entries
        .filter((e) => e.kind === "user")
        .filter((e) => (showDeleted ? true : e.accountStatus !== "deleted"))
        .filter(matchesSearch)
        .sort((a, b) => (a.email || "").localeCompare(b.email || "")),
    [entries, matchesSearch, showDeleted]
  );

  const nonUserEntries = useMemo(
    () =>
      entries
        .filter((e) => e.kind !== "user")
        .filter(matchesSearch)
        .sort((a, b) => (a.email || "").localeCompare(b.email || "")),
    [entries, matchesSearch]
  );

  const filteredBackups = useMemo(
    () =>
      backups
        .filter(matchesSearch)
        .sort((a, b) => (a.email || "").localeCompare(b.email || "")),
    [backups, matchesSearch]
  );

  const counts = useMemo(
    () => ({
      users: entries.filter((e) => e.kind === "user").length,
      active: entries.filter(
        (e) => e.kind === "user" && e.accountStatus !== "deleted"
      ).length,
      deleted: entries.filter(
        (e) => e.kind === "user" && e.accountStatus === "deleted"
      ).length,
      nonUsers: entries.filter((e) => e.kind !== "user").length,
      backups: backups.length,
    }),
    [entries, backups]
  );

  /* ----- actions --------------------------------------------------------- */

  /* ----- sync -------------------------------------------------------------
   * Runs entirely client-side. An admin can already list /users and write
   * emailDirectory under the deployed rules, so this needs no Cloud Function
   * and cannot be broken by a CORS/IAM problem.
   * ---------------------------------------------------------------------- */

  const syncFromUsers = useCallback(
    async ({ silent = false, overwrite = false } = {}) => {
      if (!silent) setBusy("Reading accounts…");

      try {
        const usersSnap = await getDocs(collection(db, "users"));

        const dirSnap = await getDocs(collection(db, "emailDirectory"));
        const existing = new Map();
        dirSnap.docs.forEach((d) => existing.set(d.id, d.data()));

        let batch = writeBatch(db);
        let ops = 0;
        let added = 0;
        let updated = 0;
        let skipped = 0;

        for (const d of usersSnap.docs) {
          const u = d.data();

          if (isExcludedFromDirectory(u)) {
            skipped += 1;
            continue;
          }

          const email = normalizeEmail(u.email);
          const id = emailDocId(email);
          const prev = existing.get(id);

          // Already recorded as a user and we're not refreshing fields.
          if (prev && prev.kind === "user" && !overwrite) {
            skipped += 1;
            continue;
          }

          const payload = {
            email,
            kind: "user",
            accountStatus: "active",
            uid: d.id,
            sirName: u.sirName || "",
            firstName: u.firstName || "",
            lastName: u.lastName || "",
            schoolName: u.schoolName || "",
            schoolDistrict: u.schoolDistrict || "",
            state: u.state || "",
            grade: u.grade || "",
            role: u.role || "",
            educatorCode: u.educatorCode || "",
            signedUpAt: u.createdAt || u.joinDate || null,
            updatedAt: serverTimestamp(),
          };

          if (prev && prev.kind === "nonUser") {
            // An outreach contact who converted — keep the history.
            payload.convertedFromNonUser = true;
            payload.convertedAt = serverTimestamp();
            payload.outreachNotes = prev.notes || "";
            payload.source = prev.source || "outreach";
            updated += 1;
          } else if (prev) {
            updated += 1;
          } else {
            payload.source = "signup";
            payload.createdAt = serverTimestamp();
            added += 1;
          }

          batch.set(doc(db, "emailDirectory", id), payload, { merge: true });
          ops += 1;

          if (ops >= 400) {
            // eslint-disable-next-line no-await-in-loop
            await batch.commit();
            batch = writeBatch(db);
            ops = 0;
          }
        }

        if (ops > 0) await batch.commit();

        if (added > 0 || updated > 0) await loadDirectory();

        return { scanned: usersSnap.size, added, updated, skipped };
      } catch (err) {
        console.error("Directory sync failed:", err);
        if (!silent) {
          alert(
            `Sync failed: ${err.message}\n\n` +
              "This page needs the emailDirectory rules deployed:\n" +
              "firebase deploy --only firestore:rules"
          );
        }
        return null;
      } finally {
        if (!silent) setBusy("");
      }
    },
    [db, loadDirectory]
  );

  // Auto-populate: anyone with an account who isn't in the directory yet gets
  // added on load, so the Users section maintains itself.
  useEffect(() => {
    if (loading || autoSyncDone) return;
    setAutoSyncDone(true);

    (async () => {
      const res = await syncFromUsers({ silent: true });
      if (res && res.added > 0) {
        setAutoSyncNote(
          `Added ${res.added} account${res.added === 1 ? "" : "s"} to the Users section.`
        );
      }
    })();
  }, [loading, autoSyncDone, syncFromUsers]);

  const handleSync = async () => {
    if (
      !window.confirm(
        "Refresh the Users section from every account in /users?\n\n" +
          "New accounts are added and existing rows are refreshed with current " +
          "school, role and code details. Student and child accounts are skipped. " +
          "Non-user contacts are left alone."
      )
    )
      return;

    const res = await syncFromUsers({ overwrite: true });
    if (res) {
      alert(
        `Sync complete.\n\nAccounts scanned: ${res.scanned}\n` +
          `Added: ${res.added}\nRefreshed: ${res.updated}\n` +
          `Skipped (students / no email): ${res.skipped}`
      );
    }
  };

  const handleAddContact = async () => {
    const email = normalizeEmail(newContact.email);
    if (!EMAIL_RE.test(email)) {
      alert("Please enter a valid email address.");
      return;
    }

    const existing = entries.find((e) => e.id === emailDocId(email));
    if (existing) {
      alert(
        `${email} is already in the ${
          existing.kind === "user" ? "Users" : "Non-Users"
        } section.`
      );
      return;
    }

    setBusy("Saving contact…");
    try {
      await setDoc(doc(db, "emailDirectory", emailDocId(email)), {
        ...newContact,
        email,
        kind: "nonUser",
        accountStatus: "prospect",
        source: newContact.source || "outreach",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setNewContact(BLANK_CONTACT);
      setShowAdd(false);
      await loadDirectory();
    } catch (err) {
      console.error("Add contact failed:", err);
      alert(`Could not save contact: ${err.message}`);
    } finally {
      setBusy("");
    }
  };

  const handlePreviewImport = () => {
    const parsed = parseContactBlob(importText);
    const known = new Set(entries.map((e) => e.id));
    setImportPreview(
      parsed.map((p) => ({
        ...p,
        duplicate: known.has(emailDocId(p.email)),
      }))
    );
  };

  const handleRunImport = async () => {
    const toAdd = importPreview.filter((p) => !p.duplicate);
    if (toAdd.length === 0) {
      alert("Nothing new to import.");
      return;
    }

    setBusy(`Importing ${toAdd.length} contacts…`);
    try {
      for (const contact of toAdd) {
        // eslint-disable-next-line no-await-in-loop
        await setDoc(doc(db, "emailDirectory", emailDocId(contact.email)), {
          email: normalizeEmail(contact.email),
          firstName: contact.firstName || "",
          lastName: contact.lastName || "",
          schoolName: contact.schoolName || "",
          schoolDistrict: contact.schoolDistrict || "",
          state: contact.state || "",
          notes: contact.notes || "",
          kind: "nonUser",
          accountStatus: "prospect",
          source: contact.source || "outreach-import",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      alert(
        `Imported ${toAdd.length} contact(s). ` +
          `${importPreview.length - toAdd.length} duplicate(s) skipped.`
      );
      setImportText("");
      setImportPreview([]);
      setShowImport(false);
      await loadDirectory();
    } catch (err) {
      console.error("Import failed:", err);
      alert(`Import failed: ${err.message}`);
    } finally {
      setBusy("");
    }
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    setBusy("Saving…");
    try {
      await updateDoc(doc(db, "emailDirectory", editing.id), {
        firstName: editing.firstName || "",
        lastName: editing.lastName || "",
        schoolName: editing.schoolName || "",
        schoolDistrict: editing.schoolDistrict || "",
        state: editing.state || "",
        source: editing.source || "",
        notes: editing.notes || "",
        updatedAt: serverTimestamp(),
      });
      setEditing(null);
      await loadDirectory();
    } catch (err) {
      console.error("Save failed:", err);
      alert(`Could not save: ${err.message}`);
    } finally {
      setBusy("");
    }
  };

  const handleRemoveEntry = async (entry) => {
    const isUser = entry.kind === "user";
    if (
      !window.confirm(
        `Remove ${entry.email} from the directory?\n\n` +
          (isUser
            ? "This only removes the directory record. It does NOT delete " +
              "their account — use Admin ▸ Roles for that.\n\n"
            : "") +
          "This cannot be undone."
      )
    )
      return;

    setBusy("Removing…");
    try {
      await deleteDoc(doc(db, "emailDirectory", entry.id));
      await loadDirectory();
    } catch (err) {
      console.error("Remove failed:", err);
      alert(`Could not remove: ${err.message}`);
    } finally {
      setBusy("");
    }
  };

  const handleMoveToNonUsers = async (entry) => {
    if (
      !window.confirm(
        `Move ${entry.email} to the Non-Users section?\n\n` +
          "Use this for addresses that never became accounts."
      )
    )
      return;

    setBusy("Moving…");
    try {
      await updateDoc(doc(db, "emailDirectory", entry.id), {
        kind: "nonUser",
        accountStatus: "prospect",
        updatedAt: serverTimestamp(),
      });
      await loadDirectory();
    } catch (err) {
      console.error("Move failed:", err);
      alert(`Could not move: ${err.message}`);
    } finally {
      setBusy("");
    }
  };

  const exportUsers = () =>
    downloadCsv(
      `sbl-users-${new Date().toISOString().slice(0, 10)}.csv`,
      userEntries,
      [
        "email",
        "firstName",
        "lastName",
        "schoolName",
        "schoolDistrict",
        "state",
        "grade",
        "role",
        "educatorCode",
        "accountStatus",
        "source",
      ]
    );

  const exportNonUsers = () =>
    downloadCsv(
      `sbl-non-users-${new Date().toISOString().slice(0, 10)}.csv`,
      nonUserEntries,
      [
        "email",
        "firstName",
        "lastName",
        "schoolName",
        "schoolDistrict",
        "state",
        "source",
        "notes",
      ]
    );

  const exportBackups = () =>
    downloadCsv(
      `sbl-educator-backups-${new Date().toISOString().slice(0, 10)}.csv`,
      filteredBackups,
      [
        "email",
        "firstName",
        "lastName",
        "sirName",
        "schoolName",
        "schoolDistrict",
        "state",
        "grade",
        "role",
        "educatorCode",
        "uid",
        "notes",
      ]
    );

  const downloadBackupJson = (backup) => {
    const blob = new Blob([JSON.stringify(backup, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `educator-backup-${backup.email || backup.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ----- render ---------------------------------------------------------- */

  return (
    <div className="ae-page">
      <div className="ae-topbar">
        <button className="ae-btn ae-btn-dark" onClick={() => navigate("/admin")}>
          ← Admin Dashboard
        </button>
        <h1 className="ae-title">Email Directory</h1>
        <div className="ae-topbar-actions">
          <button className="ae-btn ae-btn-blue" onClick={handleSync} disabled={!!busy}>
            Refresh from accounts
          </button>
          <button className="ae-btn" onClick={loadDirectory} disabled={!!busy}>
            Refresh
          </button>
        </div>
      </div>

      {busy && <div className="ae-busy">{busy}</div>}

      {autoSyncNote && (
        <div className="ae-autosync">
          {autoSyncNote}
          <button className="ae-mini" onClick={() => setAutoSyncNote("")}>
            Dismiss
          </button>
        </div>
      )}

      <div className="ae-stats">
        <div className="ae-stat">
          <span className="ae-stat-num">{counts.active}</span>
          <span className="ae-stat-label">Active users</span>
        </div>
        <div className="ae-stat">
          <span className="ae-stat-num">{counts.deleted}</span>
          <span className="ae-stat-label">Deleted users</span>
        </div>
        <div className="ae-stat">
          <span className="ae-stat-num">{counts.nonUsers}</span>
          <span className="ae-stat-label">Non-users</span>
        </div>
        <div className="ae-stat">
          <span className="ae-stat-num">{counts.backups}</span>
          <span className="ae-stat-label">Educator backups</span>
        </div>
      </div>

      <div className="ae-tabs">
        <button
          className={`ae-tab ${tab === "users" ? "is-active" : ""}`}
          onClick={() => setTab("users")}
        >
          Users ({counts.users})
        </button>
        <button
          className={`ae-tab ${tab === "nonUsers" ? "is-active" : ""}`}
          onClick={() => setTab("nonUsers")}
        >
          Non-Users ({counts.nonUsers})
        </button>
        <button
          className={`ae-tab ${tab === "backups" ? "is-active" : ""}`}
          onClick={() => setTab("backups")}
        >
          Educator Backups ({counts.backups})
        </button>
      </div>

      <div className="ae-toolbar">
        <input
          className="ae-search"
          placeholder="Search email, name, school, district, state…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {tab === "users" && (
          <>
            <label className="ae-check">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
              />
              Show deleted accounts
            </label>
            <button className="ae-btn" onClick={exportUsers}>
              Export CSV
            </button>
          </>
        )}

        {tab === "nonUsers" && (
          <>
            <button className="ae-btn ae-btn-green" onClick={() => setShowAdd(true)}>
              + Add contact
            </button>
            <button className="ae-btn ae-btn-blue" onClick={() => setShowImport(true)}>
              Bulk import
            </button>
            <button className="ae-btn" onClick={exportNonUsers}>
              Export CSV
            </button>
          </>
        )}

        {tab === "backups" && (
          <button className="ae-btn" onClick={exportBackups}>
            Export CSV
          </button>
        )}
      </div>

      {loading ? (
        <div className="ae-empty">Loading directory…</div>
      ) : (
        <div className="ae-table-wrap">
          {/* ---------------- USERS ---------------- */}
          {tab === "users" && (
            <table className="ae-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>School</th>
                  <th>District</th>
                  <th>State</th>
                  <th>Role</th>
                  <th>Code</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {userEntries.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="ae-empty-cell">
                      No user emails yet. Accounts are added automatically —
                      click "Refresh from accounts" if this looks out of date.
                    </td>
                  </tr>
                ) : (
                  userEntries.map((row) => (
                    <tr key={row.id}>
                      <td className="ae-email">{row.email}</td>
                      <td>
                        {[row.firstName, row.lastName].filter(Boolean).join(" ") ||
                          "—"}
                      </td>
                      <td>{row.schoolName || "—"}</td>
                      <td>{row.schoolDistrict || "—"}</td>
                      <td>{row.state || "—"}</td>
                      <td>{row.role || "—"}</td>
                      <td>{row.educatorCode || "—"}</td>
                      <td>
                        {row.accountStatus === "deleted" ? (
                          <span className="ae-badge ae-badge-red">
                            Deleted {fmtDate(row.deletedAt)}
                          </span>
                        ) : (
                          <span className="ae-badge ae-badge-green">Active</span>
                        )}
                        {row.convertedFromNonUser && (
                          <span className="ae-badge ae-badge-blue">Converted</span>
                        )}
                      </td>
                      <td className="ae-actions">
                        <button
                          className="ae-mini ae-mini-blue"
                          onClick={() => setEditing(row)}
                        >
                          Edit
                        </button>
                        <button
                          className="ae-mini ae-mini-amber"
                          onClick={() => handleMoveToNonUsers(row)}
                        >
                          → Non-user
                        </button>
                        <button
                          className="ae-mini ae-mini-red"
                          onClick={() => handleRemoveEntry(row)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {/* ---------------- NON-USERS ---------------- */}
          {tab === "nonUsers" && (
            <table className="ae-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>School</th>
                  <th>District</th>
                  <th>State</th>
                  <th>Source</th>
                  <th>Notes</th>
                  <th>Added</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {nonUserEntries.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="ae-empty-cell">
                      No outreach contacts yet. Use "Add contact" or "Bulk import"
                      for people you have reached out to who have not signed up.
                    </td>
                  </tr>
                ) : (
                  nonUserEntries.map((row) => (
                    <tr key={row.id}>
                      <td className="ae-email">{row.email}</td>
                      <td>
                        {[row.firstName, row.lastName].filter(Boolean).join(" ") ||
                          "—"}
                      </td>
                      <td>{row.schoolName || "—"}</td>
                      <td>{row.schoolDistrict || "—"}</td>
                      <td>{row.state || "—"}</td>
                      <td>{row.source || "—"}</td>
                      <td className="ae-notes">{row.notes || "—"}</td>
                      <td>{fmtDate(row.createdAt)}</td>
                      <td className="ae-actions">
                        <button
                          className="ae-mini ae-mini-blue"
                          onClick={() => setEditing(row)}
                        >
                          Edit
                        </button>
                        <button
                          className="ae-mini ae-mini-red"
                          onClick={() => handleRemoveEntry(row)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {/* ---------------- BACKUPS ---------------- */}
          {tab === "backups" && (
            <>
              <p className="ae-note">
                Snapshots saved automatically when an educator account is deleted
                from Admin ▸ Roles. Student and child records are never backed up.
              </p>
              <table className="ae-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>School</th>
                    <th>District</th>
                    <th>State</th>
                    <th>Grade</th>
                    <th>Code</th>
                    <th>Deleted</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBackups.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="ae-empty-cell">
                        No educator backups yet.
                      </td>
                    </tr>
                  ) : (
                    filteredBackups.map((row) => (
                      <tr key={row.id}>
                        <td className="ae-email">{row.email}</td>
                        <td>
                          {[row.sirName, row.firstName, row.lastName]
                            .filter(Boolean)
                            .join(" ") || "—"}
                        </td>
                        <td>{row.schoolName || "—"}</td>
                        <td>{row.schoolDistrict || "—"}</td>
                        <td>{row.state || "—"}</td>
                        <td>{row.grade || "—"}</td>
                        <td>{row.educatorCode || "—"}</td>
                        <td>{fmtDate(row.deletedAt)}</td>
                        <td className="ae-actions">
                          <button
                            className="ae-mini ae-mini-teal"
                            onClick={() => downloadBackupJson(row)}
                          >
                            Download JSON
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* ---------------- ADD CONTACT MODAL ---------------- */}
      {showAdd && (
        <div className="ae-modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="ae-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Add outreach contact</h2>
            <p className="ae-modal-sub">
              For someone you have contacted who has not signed up. If they
              register later, they move to the Users section automatically.
            </p>

            <div className="ae-form-grid">
              <label>
                Email *
                <input
                  type="email"
                  value={newContact.email}
                  onChange={(e) =>
                    setNewContact({ ...newContact, email: e.target.value })
                  }
                />
              </label>
              <label>
                First name
                <input
                  value={newContact.firstName}
                  onChange={(e) =>
                    setNewContact({ ...newContact, firstName: e.target.value })
                  }
                />
              </label>
              <label>
                Last name
                <input
                  value={newContact.lastName}
                  onChange={(e) =>
                    setNewContact({ ...newContact, lastName: e.target.value })
                  }
                />
              </label>
              <label>
                School
                <input
                  value={newContact.schoolName}
                  onChange={(e) =>
                    setNewContact({ ...newContact, schoolName: e.target.value })
                  }
                />
              </label>
              <label>
                District
                <input
                  value={newContact.schoolDistrict}
                  onChange={(e) =>
                    setNewContact({
                      ...newContact,
                      schoolDistrict: e.target.value,
                    })
                  }
                />
              </label>
              <label>
                State
                <input
                  maxLength={2}
                  value={newContact.state}
                  onChange={(e) =>
                    setNewContact({
                      ...newContact,
                      state: e.target.value.toUpperCase(),
                    })
                  }
                />
              </label>
              <label>
                Source
                <input
                  placeholder="conference, cold email, referral…"
                  value={newContact.source}
                  onChange={(e) =>
                    setNewContact({ ...newContact, source: e.target.value })
                  }
                />
              </label>
            </div>

            <label className="ae-full">
              Notes
              <textarea
                rows={3}
                value={newContact.notes}
                onChange={(e) =>
                  setNewContact({ ...newContact, notes: e.target.value })
                }
              />
            </label>

            <div className="ae-modal-actions">
              <button className="ae-btn ae-btn-green" onClick={handleAddContact}>
                Save contact
              </button>
              <button className="ae-btn" onClick={() => setShowAdd(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- IMPORT MODAL ---------------- */}
      {showImport && (
        <div className="ae-modal-overlay" onClick={() => setShowImport(false)}>
          <div className="ae-modal ae-modal-wide" onClick={(e) => e.stopPropagation()}>
            <h2>Bulk import contacts</h2>
            <p className="ae-modal-sub">
              Paste one email per line, or a CSV with a header row using any of:
              email, firstName, lastName, schoolName, schoolDistrict, state,
              source, notes.
            </p>

            <textarea
              className="ae-import-box"
              rows={10}
              placeholder={"principal@school.org\nteacher@district.k12.va.us"}
              value={importText}
              onChange={(e) => {
                setImportText(e.target.value);
                setImportPreview([]);
              }}
            />

            <div className="ae-modal-actions">
              <button className="ae-btn ae-btn-blue" onClick={handlePreviewImport}>
                Preview
              </button>
              <button
                className="ae-btn ae-btn-green"
                onClick={handleRunImport}
                disabled={importPreview.length === 0}
              >
                Import{" "}
                {importPreview.filter((p) => !p.duplicate).length || ""} contacts
              </button>
              <button className="ae-btn" onClick={() => setShowImport(false)}>
                Cancel
              </button>
            </div>

            {importPreview.length > 0 && (
              <div className="ae-preview">
                <strong>
                  {importPreview.length} parsed ·{" "}
                  {importPreview.filter((p) => p.duplicate).length} already in
                  directory
                </strong>
                <ul>
                  {importPreview.slice(0, 50).map((p, i) => (
                    <li key={i} className={p.duplicate ? "ae-dup" : ""}>
                      {p.email}
                      {p.duplicate ? " — duplicate, will skip" : ""}
                    </li>
                  ))}
                </ul>
                {importPreview.length > 50 && (
                  <em>…and {importPreview.length - 50} more</em>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------------- EDIT MODAL ---------------- */}
      {editing && (
        <div className="ae-modal-overlay" onClick={() => setEditing(null)}>
          <div className="ae-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Edit {editing.email}</h2>

            <div className="ae-form-grid">
              <label>
                First name
                <input
                  value={editing.firstName || ""}
                  onChange={(e) =>
                    setEditing({ ...editing, firstName: e.target.value })
                  }
                />
              </label>
              <label>
                Last name
                <input
                  value={editing.lastName || ""}
                  onChange={(e) =>
                    setEditing({ ...editing, lastName: e.target.value })
                  }
                />
              </label>
              <label>
                School
                <input
                  value={editing.schoolName || ""}
                  onChange={(e) =>
                    setEditing({ ...editing, schoolName: e.target.value })
                  }
                />
              </label>
              <label>
                District
                <input
                  value={editing.schoolDistrict || ""}
                  onChange={(e) =>
                    setEditing({ ...editing, schoolDistrict: e.target.value })
                  }
                />
              </label>
              <label>
                State
                <input
                  maxLength={2}
                  value={editing.state || ""}
                  onChange={(e) =>
                    setEditing({ ...editing, state: e.target.value.toUpperCase() })
                  }
                />
              </label>
              <label>
                Source
                <input
                  value={editing.source || ""}
                  onChange={(e) =>
                    setEditing({ ...editing, source: e.target.value })
                  }
                />
              </label>
            </div>

            <label className="ae-full">
              Notes
              <textarea
                rows={3}
                value={editing.notes || ""}
                onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
              />
            </label>

            <div className="ae-modal-actions">
              <button className="ae-btn ae-btn-green" onClick={handleSaveEdit}>
                Save
              </button>
              <button className="ae-btn" onClick={() => setEditing(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEmails;
